import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Parameters} from 'platform/components';
import {Box, Grid, HStack, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber, useFormatPercentage} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isEmpty, isNil, isNotNil, reject} from 'ramda';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleImage} from '../../../../../components/VehicleImage/VehicleImage';
import {useCatalogue} from '../../../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../../../i18n/i18n';
import {useGetPriceReportCarDetailQuery} from '../../../../../store/priceReportApi';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {getDaysOnStock} from '../../../../../utils/getDaysOnStock';
import {getDifference} from '../../../../../utils/getDifference';
import {getPrice} from '../../../../../utils/getPrice';
import {mapMetadaCurrency} from '../../../../../utils/mapMetadaCurrency';
import {powerFormatter} from '../../../../../utils/powerFormatter';
import {ExtendedStatisticsCar} from '../types/ExtendedStatisticsCar';
import {ComparedValue} from './ComparedValue';

interface CarTooltipProps extends RequiredTestIdProps {
  priceReport: PriceReportType;
  car: ExtendedStatisticsCar | Nullish;
  showShadow?: boolean;
}

export function CarTooltip(props: CarTooltipProps) {
  const formatPercentage = useFormatPercentage();
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();

  const {data, isFetching, isError} = useGetPriceReportCarDetailQuery(
    isNotNil(props.car) ? {reportId: props.priceReport.id, vehicleId: props.car.ad_id} : skipToken,
    {skip: props.car?.isMyVehicle}
  );

  const {data: translatedVehicleInfo} = useVehicleTranslatedInfo(
    isNotNil(data?.model)
      ? {...props.priceReport?.vehicle, modelFamily: data.model}
      : props.priceReport?.vehicle
  );

  const {getTransmission, getSellerType, getFuelType, getDrive, getCarStyle} = useCatalogue();

  if (isNil(props.car) || props.car.isMyVehicle) {
    return null;
  }

  const vehicleDotListItems: string[] = reject(isEmpty, [
    getTransmission(data?.transmission),
    getFuelType(data?.fuel_type),
    powerFormatter(data?.power),
    getDrive(data?.drive_type),
    getCarStyle(data?.car_style),
  ]);

  const daysInAdvertisement = isNotNil(data) ? getDaysOnStock(data, props.priceReport) : 1;

  const advertisementDotListItems: string[] = reject(isEmpty, [
    i18n.t('general.labels.nDaysInAdvertisement', {count: daysInAdvertisement}),
    getSellerType(data?.seller?.type),
  ]);

  const yearDifference = getDifference(
    data?.year,
    props.priceReport.vehicle.firstRegistrationOnYear
  );

  const featureScoreDifference = getDifference(
    data?.feature_score ?? 0,
    props.priceReport.featuresLevel
  );

  const mileageDifference = getDifference(data?.mileage, props.priceReport.vehicle.mileage);

  const vehicleName = match([data?.ad_title, !!props.car.isMyVehicle])
    .with([Pattern.nonNullable, false], () => data?.ad_title)
    .otherwise(() => `${translatedVehicleInfo.make} ${translatedVehicleInfo.model}`);
  const vehiclePrice = getPrice(data, props.priceReport);
  const priceDifference = getDifference(
    vehiclePrice,
    props.priceReport.sellingPrice.recommended.value
  );
  const currency = props.priceReport.sellingPrice.recommended.currency;

  return (
    <Box
      backgroundColor="general.white"
      borderRadius="medium"
      boxShadow={props.showShadow ? 'elevation_3' : undefined}
      border="1px solid"
      borderColor="palettes.neutral.40.100"
      overflow="hidden"
      minWidth={130}
    >
      <DataStatus isLoading={isFetching} isError={isError} minHeight={34}>
        <HStack height="100%">
          <VehicleImage
            priceReport={props.priceReport}
            // TODO T20-98639 Revert to just "data" once the backend is fixed
            vehicle={isNotNil(data) ? {...data, is_sold: props.car.is_sold} : undefined}
            isMyVehicle={false}
            maxWidth={45}
            maxHeight={34}
          />
          <Box padding={4} paddingBottom={2} height={34}>
            <VStack justify="space-between" height="100%">
              <Box flexGrow={1}>
                <Text size="small" alternative>
                  {vehicleName}
                </Text>
                <Parameters
                  parameters={vehicleDotListItems}
                  color="secondary"
                  size="xSmall"
                  data-testid={suffixTestId('vehicleParameters', props)}
                />
                <Space vertical={1} />
                <Grid spacing={4} columns={4}>
                  <ComparedValue
                    value={data?.year ?? 0}
                    difference={yearDifference}
                    data-testid={suffixTestId('year', props)}
                  />
                  <ComparedValue
                    value={data?.feature_score ?? 0}
                    difference={featureScoreDifference}
                    rankValue={(data?.feature_score ?? 0) * 100}
                    formatter={(value) => formatPercentage(value, 'down')}
                    data-testid={suffixTestId('featureScore', props)}
                  />
                  <ComparedValue
                    value={data?.mileage ?? 0}
                    difference={mileageDifference}
                    formatter={(value) => `${formatNumber(value)} ${i18n.t('general.metric.km')}`}
                    isReverseColor
                    data-testid={suffixTestId('mileage', props)}
                  />
                  <ComparedValue
                    value={vehiclePrice}
                    difference={priceDifference}
                    firstScrape={data?.first_scraped_price_with_vat}
                    formatter={(value) => formatCurrency(value, currency)}
                    firstScrapeFormatter={(value) =>
                      formatCurrency(
                        value,
                        mapMetadaCurrency(data?.first_scraped_price_currency) ?? currency
                      )
                    }
                    isReverseColor
                    isAlignEnd
                    data-testid={suffixTestId('price', props)}
                  />
                </Grid>
              </Box>
              <Parameters
                parameters={advertisementDotListItems}
                color="secondary"
                size="xSmall"
                data-testid={suffixTestId('advertisementVehicleParameters', props)}
              />
            </VStack>
          </Box>
        </HStack>
      </DataStatus>
    </Box>
  );
}
